import {
  filesOrFormField,
  sortValues,
  statusField,
} from '../components/CombinedHistoryFields';
import {
  COMBINED_HISTORY_COLUMNS,
  COMBINED_HISTORY_DIRECTIONS,
  COMBINED_HISTORY_PAGE_SIZE,
  COMBINED_HISTORY_PARAMS,
  COMBINED_HISTORY_SORT_KEYS,
  COMBINED_HISTORY_STATUS_FILTERS,
  SEARCH_PARAMS,
  SORT_BY,
} from './constants';

/**
 * @file Sorting, paging, and URL state for the combined submission history,
 *   which keeps the whole merged list in the browser. Kept apart from
 *   `combinedHistory.js` because this reads the row fields in
 *   `CombinedHistoryFields`, which reads that module.
 */

/**
 * @typedef {import('./combinedHistory').CombinedHistorySubmission} CombinedHistorySubmission
 */

/**
 * @typedef {Object} HistoryParams
 * @property {number} page 1-based.
 * @property {string|null} sortKey One of `COMBINED_HISTORY_SORT_KEYS`, or
 *   null for the loader's order.
 * @property {string|null} direction One of `COMBINED_HISTORY_DIRECTIONS`,
 *   or null with no sort.
 * @property {string|null} status A status filter, or null for all.
 * @property {string|null} form A submission type, a form number or
 *   `COMBINED_HISTORY_FILES_FILTER`, or null for all.
 * @property {string|null} date A submitted date as `YYYY-MM-DD`, or null.
 */

/**
 * The server-paged list's `sort` values, so an old link opens in the order
 * it named. `newest` is the loader's own order, so it reads as no sort and
 * the summary claims none; `oldest` reads as a date sort.
 *
 * @type {Readonly<Record<string, { sortKey: string|null, direction: string|null }>>}
 */
const LEGACY_SORTS = Object.freeze({
  [SORT_BY.NEWEST]: { sortKey: null, direction: null },
  [SORT_BY.OLDEST]: {
    sortKey: 'date',
    direction: COMBINED_HISTORY_DIRECTIONS.ASC,
  },
});

const DIRECTIONS = Object.values(COMBINED_HISTORY_DIRECTIONS);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * @param {number} year Four digits.
 * @param {number} month 1 to 12.
 * @param {number} day 1 to 31.
 * @returns {string|null} The date as `YYYY-MM-DD` when the three make a
 *   real calendar date, else null, so February 30 and a three-digit year
 *   read as no date. The Filters dialog and the URL reader share this.
 */
export const isoDateFrom = (year, month, day) => {
  if (!Number.isInteger(year) || year < 1000 || year > 9999) return null;
  if (!Number.isInteger(month) || !Number.isInteger(day)) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  const isReal =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  return isReal
    ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
        2,
        '0',
      )}`
    : null;
};

/**
 * @param {string|null} value A `date` parameter.
 * @returns {string|null} The value when it is a real date as `YYYY-MM-DD`,
 *   else null.
 */
const readIsoDate = value => {
  if (!value || !ISO_DATE.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return isoDateFrom(year, month, day);
};

/**
 * @param {string} value A column's `data-sort-value`.
 * @returns {boolean} Whether va-table reads it as a number: an empty value,
 *   or one `parseFloat` can read.
 */
const isNumericSortValue = value =>
  value === '' || !Number.isNaN(parseFloat(value));

/**
 * @param {string} value A numeric `data-sort-value`.
 * @returns {number} The number va-table compares, with an empty value as
 *   the lowest.
 */
const sortNumber = value => (value === '' ? -Infinity : parseFloat(value));

/**
 * Compares two rows' sort values as va-table does (`getCompareFunc` in the
 * component library's `sort/utils.js`), so the order the history computes
 * is the order va-table would give the same rows, and va-table's own sort
 * of a page leaves it as it is. va-table picks a column's comparator from
 * its first row's value: numbers compare as numbers, and anything else as
 * text through `Intl.Collator`. The date column carries epoch milliseconds,
 * or an empty string that va-table reads as the lowest number; the other
 * columns carry text, mostly with the `#` prefix `textSortValue` adds to
 * keep va-table from reading a value as a number or a date.
 *
 * @param {string} sample The first row's sort value.
 * @param {boolean} ascending
 * @returns {(a: SortEntry, b: SortEntry) => number}
 */
const comparatorFor = (sample, ascending) => {
  if (isNumericSortValue(sample)) {
    return (a, b) => {
      const delta = ascending ? a.number - b.number : b.number - a.number;
      return Number.isNaN(delta) ? 0 : delta;
    };
  }
  const collator = new Intl.Collator('en-US', { sensitivity: 'base' });
  return (a, b) =>
    ascending
      ? collator.compare(a.value, b.value)
      : collator.compare(b.value, a.value);
};

/**
 * @typedef {Object} SortEntry
 * @property {CombinedHistorySubmission} submission
 * @property {string} value The row's sort value for the column.
 * @property {number} number The value as va-table's numeric comparator
 *   reads it, parsed once rather than on every comparison.
 */

/**
 * @param {string|null} sortKey
 * @returns {number} The column index the key names, or -1.
 */
export const sortColumnIndex = sortKey =>
  COMBINED_HISTORY_SORT_KEYS.indexOf(sortKey);

const STATUS_COLUMN = COMBINED_HISTORY_SORT_KEYS.indexOf('status');
const FILES_COLUMN = COMBINED_HISTORY_SORT_KEYS.indexOf('files');
const BLANK_FIELD = { sortValue: '' };

/**
 * @param {CombinedHistorySubmission} submission
 * @param {number} column The column being sorted.
 * @returns {string} The row's `data-sort-value` for that column, as the
 *   table sets it. Only the status and files fields cost anything to build,
 *   so each is built only for its own column.
 */
const sortValueFor = (submission, column) =>
  sortValues(submission, {
    status: column === STATUS_COLUMN ? statusField(submission) : BLANK_FIELD,
    filesOrForm:
      column === FILES_COLUMN ? filesOrFormField(submission) : BLANK_FIELD,
  })[column] ?? '';

/**
 * Sorts the whole history by one column, as va-table would sort the rows it
 * is given. Rows that tie keep the loader's order, as va-table's stable sort
 * keeps the order it had.
 *
 * @param {CombinedHistorySubmission[]} submissions The loader's rows.
 * @param {string|null} sortKey One of `COMBINED_HISTORY_SORT_KEYS`.
 * @param {string|null} direction One of `COMBINED_HISTORY_DIRECTIONS`.
 * @returns {CombinedHistorySubmission[]} A sorted copy, or `submissions`
 *   itself when there is no sort to apply.
 * @example
 * sortSubmissions(rows, 'date', 'asc'); // oldest first
 */
export const sortSubmissions = (submissions, sortKey, direction) => {
  const column = sortColumnIndex(sortKey);
  if (column < 0 || !DIRECTIONS.includes(direction)) return submissions;
  if (submissions.length === 0) return submissions;

  const entries = submissions.map(submission => {
    const value = sortValueFor(submission, column);
    return { submission, value, number: sortNumber(value) };
  });
  const compare = comparatorFor(
    entries[0].value,
    direction === COMBINED_HISTORY_DIRECTIONS.ASC,
  );
  return entries.sort(compare).map(entry => entry.submission);
};

/**
 * @param {CombinedHistorySubmission[]} rows The rows in the order to page.
 * @param {number} page The 1-based page asked for. Out of range is clamped.
 * @param {number} [size]
 * @returns {{ rows: CombinedHistorySubmission[], page: number, pages: number, first: number, last: number, total: number }}
 *   The page's rows, the page actually shown, how many pages there are, and
 *   the 1-based positions of the page's first and last rows (both 0 with no
 *   rows).
 */
export const paginate = (rows, page, size = COMBINED_HISTORY_PAGE_SIZE) => {
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const wanted = Number.isInteger(page) && page > 0 ? page : 1;
  const current = Math.min(wanted, pages);
  const start = (current - 1) * size;
  return {
    rows: rows.slice(start, start + size),
    page: current,
    pages,
    first: total === 0 ? 0 : start + 1,
    last: Math.min(start + size, total),
    total,
  };
};

/**
 * @param {URLSearchParams} searchParams
 * @returns {HistoryParams} The page, sort, and filters the URL names. The
 *   search text never is: see `COMBINED_HISTORY_PARAMS`. A page that is not
 *   a positive whole number reads as 1. A sort or a status the history does
 *   not know reads as none, and so does a date that is not a real
 *   `YYYY-MM-DD`; the server-paged list's `oldest` reads as a date sort and
 *   its `newest`, the loader's order, as none. A known sort with no
 *   readable direction reads ascending, as a first click on a va-table
 *   header sorts. The submission type is kept as given: a value not
 *   loaded today may be tomorrow. The search text and the
 *   confirmation number filter are never in the URL.
 */
export const readHistoryParams = searchParams => {
  const pageParam = searchParams.get(COMBINED_HISTORY_PARAMS.PAGE) ?? '';
  const page = /^\d+$/.test(pageParam) ? Number(pageParam) : NaN;
  const sort = searchParams.get(COMBINED_HISTORY_PARAMS.SORT);
  const dir = searchParams.get(COMBINED_HISTORY_PARAMS.DIR);
  const status = searchParams.get(COMBINED_HISTORY_PARAMS.STATUS);

  let sortKey = null;
  let direction = null;
  if (sort !== null && Object.hasOwn(LEGACY_SORTS, sort)) {
    ({ sortKey, direction } = LEGACY_SORTS[sort]);
  } else if (COMBINED_HISTORY_SORT_KEYS.includes(sort)) {
    sortKey = sort;
    direction = DIRECTIONS.includes(dir)
      ? dir
      : COMBINED_HISTORY_DIRECTIONS.ASC;
  }

  return {
    page: Number.isInteger(page) && page > 0 ? page : 1,
    sortKey,
    direction,
    status: COMBINED_HISTORY_STATUS_FILTERS.includes(status) ? status : null,
    form: searchParams.get(COMBINED_HISTORY_PARAMS.FORM) || null,
    date: readIsoDate(searchParams.get(COMBINED_HISTORY_PARAMS.DATE)),
  };
};

/**
 * @param {URLSearchParams} searchParams The current URL's parameters.
 * @param {HistoryParams} params The page, sort, and filters to write.
 * @returns {URLSearchParams} A copy with the history's parameters replaced.
 *   The first page, the loader's order, and an unset filter write nothing,
 *   so the URL stays as short as the state it names. The server-paged list's
 *   `perPage` is dropped. Other parameters are kept.
 */
export const historyParamsFor = (
  searchParams,
  { page, sortKey, direction, status = null, form = null, date = null },
) => {
  const next = new URLSearchParams(searchParams);
  Object.values(COMBINED_HISTORY_PARAMS).forEach(name => next.delete(name));
  next.delete(SEARCH_PARAMS.SIZE);
  if (status) next.set(COMBINED_HISTORY_PARAMS.STATUS, status);
  if (form) next.set(COMBINED_HISTORY_PARAMS.FORM, form);
  if (date) next.set(COMBINED_HISTORY_PARAMS.DATE, date);
  if (page > 1) next.set(COMBINED_HISTORY_PARAMS.PAGE, String(page));
  if (sortKey) {
    next.set(COMBINED_HISTORY_PARAMS.SORT, sortKey);
    next.set(COMBINED_HISTORY_PARAMS.DIR, direction);
  }
  return next;
};

/**
 * @param {HistoryParams} params
 * @returns {string|null} What the summary line says the rows are sorted by,
 *   such as "Submitted date (oldest first)", or null for the loader's order.
 */
export const sortLabel = ({ sortKey, direction }) => {
  const column = sortColumnIndex(sortKey);
  if (column < 0) return null;
  return COMBINED_HISTORY_COLUMNS[column].sortedBy[
    direction === COMBINED_HISTORY_DIRECTIONS.DESC
      ? COMBINED_HISTORY_DIRECTIONS.DESC
      : COMBINED_HISTORY_DIRECTIONS.ASC
  ];
};
